import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import logo from "../../../assets/images/wa-horizontal-color.png";
import { horarioBonito } from "../../../utils/horarioBonito";
import "./datos.css";

/*
	Formulario del cliente (/datos/<token>, Esteban 2026-10-19): el enlace que le llega por WhatsApp en cuanto la vendedora
	crea la Solicitud. Una pregunta por pantalla, cada paso se guarda solo (si cierra, sigue donde iba) y al terminar la
	Matrícula se arma sola y se va a Admisiones. Honduras de usted, México de tú (como la firma del contrato). Sin sesión.

	Desde el 19-oct-2026 empieza por la foto de la identificación: la IA lee nombre completo, fecha de nacimiento, número y
	sexo, y el cliente solo confirma en una pantalla (sin dedazos). Los menores no tienen identificación: sus datos van a mano,
	pero la del tutor sí se pide y llena su nombre.
*/

const API = (process.env.REACT_APP_API_URL_V2 || "") + "/formulario-cliente/";

interface Opcion { id: number; name: string; }
interface TipoId extends Opcion { min_length: number; max_length: number; bool_only_digits: number; }
interface Contacto { name: string; relation: string; contactMedia: string; }
interface Datos {
	firstName?: string; lastName?: string; birthday?: string; genderId?: number | null; identificationTypeId?: number | null;
	identificationCode?: string; nationalityId?: number | null; email?: string; countryId?: number | null; stateId?: number | null;
	cityId?: number | null; stateName?: string; cityName?: string; address?: string; academicDegreeId?: number | null;
	lastSchoolName?: string; currentOccupation?: string; contacts?: Contacto[];
}
interface Info {
	nombre: string; plantel: string; paisPlantel: number; cursos: { curso: string; horario: string }[]; datos: Datos;
	fotos: Record<string, boolean>; terminado: boolean; para: "yo" | "menor" | null; telefono: string;
	/** Varios alumnos con el mismo número (mamá con sus hijos): de cuál es este enlace y cuántos de los otros faltan. */
	grupo?: { n: number; de: number; nombre: string | null; faltanOtros: number } | null;
	catalogos: { tiposId: TipoId[]; generos: Opcion[]; paises: Opcion[]; estados: Opcion[]; grados: Opcion[]; tipoPreferido?: number | null };
}
interface Falta { texto: string; paso: Paso; }
interface Lectura { campos: Partial<Datos>; nombreCompleto: string; aviso: string | null; menorDeEdad: boolean; }
type Paso = "bienvenida" | "para" | "fotoId" | "datos" | "fotoTutor" | "tutor" | "correo" | "domicilio" | "estudios" | "ocupacion" | "contacto"
	| "revisar" | "listo";
/** Las reglas del servidor hablan de los pasos de antes (una pregunta por campo): aquí van juntos en una pantalla. */
const PASO_DE: Record<string, Paso> = {
	nombres: "datos", apellidos: "datos", nacimiento: "datos", genero: "datos", identificacion: "datos", nacionalidad: "datos",
	ubicacion: "domicilio", direccion: "domicilio"
};
type Campo = "nombres" | "apellidos" | "nacimiento" | "genero" | "identificacion" | "nacionalidad";

/** Solo los mensajes que escribimos para el cliente (JSON del formulario); nunca un error técnico o en inglés. */
const mensaje = (e: any, porDefecto: string): string => {
	const st = Number(e?.response?.status || 0);
	const m = e?.response?.data?.message;
	return typeof m === "string" && m.trim() !== "" && [400, 404, 409, 422, 500].includes(st) && !/server error|not found|unauthenticated/i.test(m) ? m : porDefecto;
};
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const PARENTESCOS = ["Mamá", "Papá", "Esposo(a)", "Hermano(a)", "Abuelo(a)", "Tío(a)", "Amigo(a)"];
const PARENTESCOS_TUTOR = ["Mamá", "Papá", "Tutor(a)", "Abuelo(a)", "Tío(a)"];

const edadDe = (iso?: string): number | null => {
	if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
	const [y, m, d] = iso.split("-").map(Number);
	const hoy = new Date();
	let edad = hoy.getFullYear() - y;
	if (hoy.getMonth() + 1 < m || (hoy.getMonth() + 1 === m && hoy.getDate() < d)) edad--;
	return edad;
};
const fechaValida = (iso?: string): boolean => {
	if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
	const [y, m, d] = iso.split("-").map(Number);
	const f = new Date(y, m - 1, d);
	return f.getFullYear() === y && f.getMonth() === m - 1 && f.getDate() === d && y >= 1930 && f < new Date();
};
/** "50477665544" → "+504 7766-5544"; "5218112345678" → "+52 81 1234 5678"; lo demás, como lo escribió. */
const telBonito = (tel: string): string => {
	const d = (tel || "").replace(/\D/g, "");
	if (d.length === 11 && d.startsWith("504")) return "+504 " + d.slice(3, 7) + "-" + d.slice(7);
	const mx = d.startsWith("521") && d.length === 13 ? d.slice(3) : d.startsWith("52") && d.length === 12 ? d.slice(2) : null;
	if (mx) return "+52 " + mx.slice(0, 2) + " " + mx.slice(2, 6) + " " + mx.slice(6);
	return tel;
};
const correoValido = (c?: string): boolean => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test((c || "").trim());
const esTutor = (rel: string): boolean => /mam|pap|madre|padre|tutor|abuel|t[ií]o|t[ií]a/i.test(rel);

/** La foto del celular pesa 4-8 MB: se baja a 1600 px en JPEG antes de subirla (si el navegador no la puede leer, va como está). */
const achicar = (archivo: File): Promise<Blob> => new Promise((resolve) => {
	// HEIC del iPhone: Safari la sabe pintar y sale en JPEG (la IA no lee HEIC); si el navegador no puede, va como está.
	if (!archivo.type.startsWith("image/") && !/\.hei[cf]$/i.test(archivo.name)) { resolve(archivo); return; }
	const url = URL.createObjectURL(archivo);
	const img = new Image();
	img.onload = () => {
		const escala = Math.min(1, 1600 / Math.max(img.width, img.height));
		const c = document.createElement("canvas");
		c.width = Math.round(img.width * escala); c.height = Math.round(img.height * escala);
		c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
		URL.revokeObjectURL(url);
		c.toBlob((b) => resolve(b && (b.size < archivo.size || /hei[cf]/i.test(archivo.type + archivo.name)) ? b : archivo), "image/jpeg", 0.85);
	};
	img.onerror = () => { URL.revokeObjectURL(url); resolve(archivo); };
	img.src = url;
});

const Chips: React.FC<{ opciones: { valor: any; texto: string }[]; valor: any; onElegir: (v: any) => void; columnas?: boolean; columnasUna?: boolean }> = ({ opciones, valor, onElegir, columnas, columnasUna }) => (
	<div className={"dcChips" + (columnas ? " columnas" : "") + (columnasUna ? " una" : "")} role="radiogroup">
		{opciones.map((o) => (
			<button type="button" role="radio" aria-checked={valor === o.valor} key={String(o.valor)} className={"dcChip" + (valor === o.valor ? " activa" : "")} onClick={() => onElegir(o.valor)}>
				<span className="dcChipPunto" aria-hidden="true" />{o.texto}
			</button>
		))}
	</div>
);

/** Un dato con su etiqueta; si lo cambiaron respecto a la identificación, se dice qué dice ella. Va fuera del formulario
	para que React no vuelva a montar los campos en cada tecla. */
const Dato: React.FC<{ etiqueta: string; enId?: string | null; onUsar?: () => void; children: React.ReactNode }> = ({ etiqueta, enId, onUsar, children }) => (
	<div className="dcDato">
		<span className="dcDatoEtiqueta">{etiqueta}</span>
		{children}
		{enId ? <span className="dcDifiere">En la identificación dice: <b>{enId}</b>{onUsar ? <button type="button" className="dcUsar" onClick={onUsar}>Usar este</button> : null}</span> : null}
	</div>
);

const Datos: React.FC<{ token: string }> = ({ token }) => {
	const [info, setInfo] = useState<Info | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [d, setD] = useState<Datos>({});
	const [fotos, setFotos] = useState<Record<string, boolean>>({});
	const [paso, setPaso] = useState<Paso>("bienvenida");
	const [aviso, setAviso] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState<boolean>(false);
	const [subiendo, setSubiendo] = useState<string | null>(null);
	const [ciudades, setCiudades] = useState<Opcion[]>([]);
	const [otraNacion, setOtraNacion] = useState<boolean>(false);
	const [otroPais, setOtroPais] = useState<boolean>(false);
	const [faltan, setFaltan] = useState<Falta[]>([]);
	// Quién llena: la persona misma o, si es menor, su mamá, papá o tutor (Esteban, 2026-10-19: "con ellos ni hablar").
	const [para, setPara] = useState<"yo" | "menor" | null>(null);
	// Aviso informativo para un paso (p. ej. "como es menor de edad, lo termina su mamá…"): se ve solo en ese paso.
	const [nota, setNota] = useState<{ paso: Paso; texto: string } | null>(null);
	const [dia, setDia] = useState(""); const [mes, setMes] = useState(""); const [anio, setAnio] = useState("");
	// Lo que la IA leyó de la identificación del alumno: se enseña como "en su identificación dice…" si lo cambian.
	const [leido, setLeido] = useState<Partial<Datos> | null>(null);
	// Lo leído por la IA siempre se enseña una vez para que lo confirme, aunque ya esté completo.
	const [confirmo, setConfirmo] = useState<boolean>(false);
	// Con el tipo de documento ya puesto (leído o el de siempre) solo se ve el número; "¿Es otro documento?" abre la lista.
	const [verTipos, setVerTipos] = useState<boolean>(false);
	const primerCampo = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		axios.get<Info>(API + token).then((r) => {
			setInfo(r.data);
			const datos = r.data.datos || {};
			setD({ countryId: r.data.paisPlantel, ...datos });
			setFotos(r.data.fotos || {});
			if (datos.birthday) { const [y, m, dd] = datos.birthday.split("-"); setAnio(y); setMes(String(Number(m))); setDia(String(Number(dd))); }
			if (datos.nationalityId && datos.nationalityId !== r.data.paisPlantel) setOtraNacion(true);
			if (datos.countryId && datos.countryId !== r.data.paisPlantel) setOtroPais(true);
			setPara(r.data.para || null);
			if (r.data.terminado) setPaso("listo");
			// Si la identificación ya estaba subida (la de la vendedora, o la suya antes de recargar), se lee mientras ve la bienvenida.
			else if (r.data.fotos?.id_frente && r.data.para !== "menor") {
				axios.post(API + token + "/_leer").then((x) => {
					const c: Partial<Datos> = x.data?.lectura?.campos || {};
					if (Object.keys(c).length === 0) return;
					// Lo que ya escribió (o ya quedó guardado) se respeta; lo leído llena lo que falte.
					setD((y) => {
						const n: any = { ...y };
						Object.entries(c).forEach(([k, v]) => { if (n[k] === undefined || n[k] === null || n[k] === "") n[k] = v; });
						if (n.birthday && /^\d{4}-\d{2}-\d{2}$/.test(n.birthday)) { const [yy, mm, dd] = n.birthday.split("-"); setAnio(yy); setMes(String(Number(mm))); setDia(String(Number(dd))); }
						if (n.nationalityId && n.nationalityId !== r.data.paisPlantel) setOtraNacion(true);
						return n;
					});
					setLeido(c);
				}).catch(() => { /* sin lectura: sigue a mano */ });
			}
		}).catch((e) => setError(mensaje(e, "No se pudo abrir el formulario. Por favor, revise su conexión e intente de nuevo.")));
	}, [token]);

	useEffect(() => {
		if (!d.stateId) { setCiudades([]); return; }
		axios.get<Opcion[]>(API + token + "/_ciudades?estado=" + d.stateId).then((r) => setCiudades(r.data)).catch(() => setCiudades([]));
	}, [d.stateId]);

	const u = info?.paisPlantel === 2;   // Honduras: de usted
	const t = (tu: string, usted: string) => (u ? usted : tu);
	// Dos cosas distintas: quién llena (porOtro: la mamá, papá o tutor, y las preguntas hablan del alumno) y qué se pide
	// (menor: datos e identificación del tutor; mayor: identificación del alumno y contacto de emergencia), que va por la edad.
	const porOtro = para === "menor";
	const menor = fechaValida(d.birthday) ? (edadDe(d.birthday) ?? 99) < 18 : porOtro;
	// En modo tutor las preguntas hablan del alumno por su nombre.
	const alumno = (d.firstName || "").split(" ")[0] || "el alumno";
	const conEstados = !otroPais && (info?.catalogos.estados.length || 0) > 0;
	const tipo = info?.catalogos.tiposId.find((x) => x.id === d.identificationTypeId) || null;
	const contacto: Contacto = (d.contacts || [])[0] || { name: "", relation: "", contactMedia: "" };
	const pais = info?.catalogos.paises.find((p) => p.id === info?.paisPlantel);

	// Adulto: la foto de su identificación primero y con ella se llenan sus datos. Menor: la identificación del tutor (llena su
	// nombre) y los datos del alumno a mano, de su partida de nacimiento.
	const pasos: Paso[] = useMemo(() => menor
		? ["bienvenida", "para", "fotoTutor", "tutor", "datos", "correo", "domicilio", "estudios", "ocupacion", "revisar"]
		: ["bienvenida", "para", "fotoId", "datos", "correo", "domicilio", "estudios", "ocupacion", "contacto", "revisar"], [menor]);
	const indice = pasos.indexOf(paso);
	const progreso = paso === "listo" ? 100 : Math.round((Math.max(0, indice) / (pasos.length - 1)) * 100);

	// El teléfono del tutor casi siempre es el del chat: se ofrece ya escrito.
	useEffect(() => { if (paso === "tutor" && info && !contacto.contactMedia) cambiarContacto({ contactMedia: info.telefono }); }, [paso]);
	// El cursor al primer campo, salvo cuando los datos ya vienen leídos de la identificación (el teclado taparía la revisión).
	useEffect(() => { setAviso(null); if (!(paso === "datos" && leido)) window.setTimeout(() => primerCampo.current?.focus(), 250); }, [paso]);
	// El documento de siempre ya elegido: en Honduras el DNI (también de los menores); en México la CURP del menor.
	useEffect(() => {
		if (paso !== "datos" || d.identificationTypeId || !info?.catalogos.tipoPreferido) return;
		if (info.paisPlantel === 2 || menor) cambiar({ identificationTypeId: Number(info.catalogos.tipoPreferido) });
	}, [paso]);

	const cambiar = (cambios: Partial<Datos>) => setD((x) => ({ ...x, ...cambios }));
	const cambiarContacto = (cambios: Partial<Contacto>) => cambiar({ contacts: [{ ...contacto, ...cambios }] });

	/** Lo que falta en un dato de la pantalla de datos. */
	const problemaCampo = (p: Campo): string | null => {
		switch (p) {
			case "nombres": return (d.firstName || "").trim().length < 2 ? (porOtro ? t("Escribe el nombre del alumno.", "Escriba el nombre del alumno.") : t("Escribe tu nombre.", "Escriba su nombre.")) : null;
			case "apellidos": return (d.lastName || "").trim().length < 2 ? (porOtro ? t("Escribe los apellidos del alumno.", "Escriba los apellidos del alumno.") : t("Escribe tus apellidos.", "Escriba sus apellidos.")) : null;
			case "nacimiento": return !fechaValida(d.birthday) ? t("Revisa el día, el mes y el año.", "Revise el día, el mes y el año.") : null;
			case "genero": return d.genderId === undefined || d.genderId === null ? t("Elige el género.", "Elija el género.") : null;
			case "identificacion": {
				if (!tipo) return t("Elige el tipo de documento.", "Elija el tipo de documento.");
				const code = (d.identificationCode || "").trim();
				if (tipo.bool_only_digits && /\D/.test(code)) return t("Escribe solo números, sin guiones.", "Escriba solo números, sin guiones.");
				if (code.length < tipo.min_length || code.length > tipo.max_length)
					return t("Revisa el número: debe llevar ", "Revise el número: debe llevar ") + (tipo.min_length === tipo.max_length ? tipo.min_length : "de " + tipo.min_length + " a " + tipo.max_length)
						+ (tipo.bool_only_digits ? " dígitos" : " caracteres") + " y tiene " + code.length + ".";
				return null;
			}
			case "nacionalidad": return !d.nationalityId ? (porOtro ? t("Elige la nacionalidad.", "Elija la nacionalidad.") : t("Elige tu nacionalidad.", "Elija su nacionalidad.")) : null;
			default: return null;
		}
	};
	const CAMPOS: Campo[] = ["nombres", "apellidos", "nacimiento", "genero", "identificacion", "nacionalidad"];

	/** Lo que falta en este paso, dicho como pregunta amable; null = puede seguir. */
	const problema = (p: Paso): string | null => {
		switch (p) {
			case "para": return para === null ? t("Elige una opción.", "Elija una opción.") : null;
			case "datos": return CAMPOS.map(problemaCampo).find((x) => x !== null) || null;
			case "fotoId": return !fotos.id_frente ? t("Sube la foto del frente de tu identificación.", "Suba la foto del frente de su identificación.") : null;
			case "correo": return !correoValido(d.email) ? t("Revisa el correo: debe verse como nombre@gmail.com.", "Revise el correo: debe verse como nombre@gmail.com.") : null;
			case "domicilio":
				if (!d.countryId) return t("Elige el país.", "Elija el país.");
				if (conEstados && !d.stateId) return info?.paisPlantel === 1 ? t("Elige el estado.", "Elija el estado.") : t("Elige el departamento.", "Elija el departamento.");
				if (conEstados && !d.cityId) return t("Elige la ciudad.", "Elija la ciudad.");
				return (d.address || "").trim().length < 6 ? t("Escribe la colonia, la calle y el número de casa.", "Escriba la colonia, la calle y el número de casa.") : null;
			case "estudios": return !d.academicDegreeId ? t("Elige el último grado de estudios.", "Elija el último grado de estudios.") : null;
			case "ocupacion": return (d.currentOccupation || "").trim().length < 3 ? (porOtro ? t("Escribe a qué se dedica.", "Escriba a qué se dedica.") : t("Escribe a qué te dedicas.", "Escriba a qué se dedica.")) : null;
			case "tutor":
			case "contacto":
				if (contacto.name.trim().split(/\s+/).length < 2) return t("Escribe nombre y apellido.", "Escriba nombre y apellido.");
				if (!contacto.relation.trim()) return t("Elige el parentesco.", "Elija el parentesco.");
				if (p === "tutor" && !esTutor(contacto.relation)) return "Por ser menor de edad, tiene que ser su mamá, papá o tutor.";
				if (contacto.contactMedia.replace(/\D/g, "").length < 8) return t("Escribe el teléfono completo.", "Escriba el teléfono completo.");
				return null;
			case "fotoTutor": return !fotos.tutor_frente ? t("Sube la foto del frente de tu identificación.", "Suba la foto del frente de su identificación.") : null;
			default: return null;
		}
	};

	const guardar = (extra: any = {}): Promise<any> => axios.post(API + token, { datos: { ...d, para, ...extra } }).catch(() => { /* se vuelve a guardar en el siguiente paso */ });

	const siguiente = async (desde: Paso = paso) => {
		const p = problema(desde);
		// Siempre con "por favor" (Esteban, 2026-10-19: "un poco más gentil").
		if (p) { setAviso(/^(Escrib|Elig|Elij|Revis|Sub|Sube)/.test(p) ? "Por favor, " + p.charAt(0).toLowerCase() + p.slice(1) : p); return; }
		if (desde !== "bienvenida") guardar();
		if (desde === "revisar") { await terminar(); return; }
		if (desde === "datos") setConfirmo(true);
		const edad = edadDe(d.birthday);
		// Dijo "para mí" pero es menor de edad: desde aquí llena su mamá, papá o tutor, empezando por su identificación.
		if (desde === "datos" && para !== "menor" && edad !== null && edad < 18) {
			setPara("menor");
			guardar({ para: "menor" });
			setNota({ paso: "fotoTutor", texto: t("Como eres menor de edad, la inscripción la termina tu mamá, papá o tutor. Por favor, pásale el celular.", "Como es menor de edad, la inscripción la termina su mamá, papá o tutor.") });
			setPaso(fotos.tutor_frente ? "tutor" : "fotoTutor");
			return;
		}
		// Dijo "para mi hijo(a)" pero ya es mayor de edad: se sigue como adulto y se pide la identificación del alumno.
		if (desde === "datos" && para === "menor" && edad !== null && edad >= 18 && !fotos.id_frente) {
			setNota({ paso: "fotoId", texto: t("Como " + alumno + " ya es mayor de edad, necesitamos también la foto de su identificación.", "Como " + alumno + " ya es mayor de edad, necesitamos también la foto de su identificación.") });
			setPaso("fotoId");
			return;
		}
		// Al entrar (o al volver con avance) va directo a la primera pregunta sin contestar.
		if (desde === "bienvenida") {
			const primera = pasos.find((x) => x !== "bienvenida" && x !== "revisar" && problema(x) !== null) || "revisar";
			setPaso(leido && !confirmo && !menor && pasos.indexOf("datos") < pasos.indexOf(primera) ? "datos" : primera);
			return;
		}
		if (faltan.length > 0) { setPaso("revisar"); return; }
		// Siguiente pregunta sin contestar: lo que ya está completo (leído de la identificación o de antes) no se vuelve a preguntar.
		const i = pasos.indexOf(desde);
		const n = pasos.slice(i + 1).find((x) => x === "revisar" || problema(x) !== null);
		if (n) setPaso(n);
	};
	const atras = () => { const n = pasos[indice - 1]; if (n) setPaso(n); };

	const terminar = async () => {
		setOcupado(true);
		try {
			await axios.post(API + token, { datos: { ...d, para } });
			await axios.post(API + token + "/_terminar");
			setFaltan([]);
			setPaso("listo");
		} catch (e: any) {
			const lista: Falta[] = (e?.response?.data?.faltan || []).map((f: Falta) => ({ ...f, paso: PASO_DE[f.paso] || f.paso }));
			if (lista.length > 0) { setFaltan(lista); setPaso(pasos.includes(lista[0].paso) ? lista[0].paso : "revisar"); }
			else setAviso(mensaje(e, t("No se pudo enviar. Por favor, revisa tu conexión e intenta de nuevo.", "No se pudo enviar. Por favor, revise su conexión e intente de nuevo.")));
		} finally {
			setOcupado(false);
		}
	};

	/** Lo leído de la identificación del alumno entra a los campos (una foto nueva manda sobre lo escrito antes). */
	const usarLectura = (campos: Partial<Datos>) => {
		setD((x) => ({ ...x, ...campos }));
		if (campos.birthday) { const [y, m, dd] = campos.birthday.split("-"); setAnio(y); setMes(String(Number(m))); setDia(String(Number(dd))); }
		if (campos.nationalityId && info) setOtraNacion(campos.nationalityId !== info.paisPlantel);
		setLeido(campos);
	};

	const subir = async (ranura: string, archivo: File | undefined) => {
		if (!archivo) return;
		setSubiendo(ranura);
		setAviso(null);
		try {
			const blob = await achicar(archivo);
			const f = new FormData();
			f.append("ranura", ranura);
			f.append("archivo", blob, blob === archivo ? archivo.name : ranura + ".jpg");
			const r = await axios.post(API + token + "/_foto", f);
			setFotos(r.data.fotos);
			const l: Lectura | null = r.data.lectura || null;
			if (l?.aviso) setAviso(l.aviso);
			if (ranura === "id_frente" && l && Object.keys(l.campos || {}).length > 0) {
				usarLectura(l.campos);
				// Sin nada que corregir en la foto, directo a confirmar los datos.
				if (!l.aviso) setPaso("datos");
			} else if (ranura === "tutor_frente" && l && l.nombreCompleto && !l.aviso) {
				cambiarContacto({ name: l.nombreCompleto });
				setPaso("tutor");
			}
		} catch (e) {
			setAviso(mensaje(e, t("No se pudo subir la foto. Por favor, intenta con otra.", "No se pudo subir la foto. Por favor, intente con otra.")));
		} finally {
			setSubiendo(null);
		}
	};

	const alEnter = (e: React.KeyboardEvent) => { if (e.key === "Enter" && !(e.target as any)?.matches?.("textarea")) { e.preventDefault(); siguiente(); } };

	const ponerFecha = (dd: string, mm: string, yy: string) => {
		setDia(dd); setMes(mm); setAnio(yy);
		cambiar({ birthday: dd && mm && yy.length === 4 ? `${yy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}` : "" });
	};

	const Foto: React.FC<{ ranura: string; titulo: string }> = ({ ranura, titulo }) => (
		<label className={"dcFoto" + (fotos[ranura] ? " lista" : "")}>
			<input type="file" accept="image/*,application/pdf" onChange={(e) => { subir(ranura, e.target.files?.[0]); e.target.value = ""; }} />
			<span className="dcFotoIcono" aria-hidden="true">{subiendo === ranura ? <span className="dcGiro" /> : fotos[ranura] ? "✓" : "+"}</span>
			<span className="dcFotoTexto">
				<b>{titulo}</b>
				<small>{subiendo === ranura ? "Leyendo la identificación…" : fotos[ranura] ? t("Lista · toca para cambiarla", "Lista · toque para cambiarla") : t("Toca para tomar o elegir la foto", "Toque para tomar o elegir la foto")}</small>
			</span>
		</label>
	);

	const norm = (x?: string | null) => (x || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
	const difiere = (k: "firstName" | "lastName" | "identificationCode"): string | null =>
		leido && leido[k] && norm(String(d[k] || "")) !== norm(String(leido[k])) ? String(leido[k]) : null;
	const fechaLarga = (iso?: string | null) => {
		if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
		const [y, m, dd] = iso.split("-").map(Number);
		return `${dd} de ${(MESES[m - 1] || "").toLowerCase()} de ${y}`;
	};

	if (error) {
		return <div className="dc"><div className="dcCabeza"><img src={logo} alt="Washington Academy" className="dcLogo" /></div><div className="dcCentro"><p className="dcError">{error}</p></div></div>;
	}
	if (!info) {
		return <div className="dc"><div className="dcCentro"><span className="dcGiro grande" /></div></div>;
	}

	const pregunta = (titulo: string, ayuda: string | null, cuerpo: React.ReactNode) => (
		<div className="dcPregunta" key={paso}>
			<div className="dcNumero">{indice} <span>de {pasos.length - 1}</span></div>
			<h1 className="dcTitulo">{titulo}</h1>
			{ayuda ? <p className="dcAyuda">{ayuda}</p> : null}
			{nota && nota.paso === paso ? <p className="dcNotaPaso">{nota.texto}</p> : null}
			{cuerpo}
		</div>
	);

	const pantalla = (): React.ReactNode => {
		const nombre = (porOtro ? contacto.name : (d.firstName || info.nombre || "")).split(" ")[0];
		switch (paso) {
			case "bienvenida": {
				const avanzado = !!(d.birthday || d.identificationCode || d.email);
				return (
					<div className="dcPregunta dcBienvenida" key="bienvenida">
						<div className="dcSaludo">👋</div>
						<h1 className="dcTitulo grande">{"¡Hola" + (nombre && para ? ", " + nombre : "") + "!"}</h1>
						<p className="dcAyuda">{t("Gracias por tu inscripción en Washington Academy. Para terminarla necesitamos unos datos; toma unos 3 minutos.", "Gracias por su inscripción en Washington Academy. Para terminarla necesitamos unos datos; toma unos 3 minutos.")}</p>
						{info.grupo ? (
							<p className="dcNota dcGrupo dcGrupoArriba">
								Este enlace es para {info.grupo.nombre ? <>los datos de <b>{info.grupo.nombre}</b></> : "uno de los alumnos"} ({info.grupo.n} de {info.grupo.de}).{" "}
								{t("A este número llegó un enlace por cada alumno.", "A este número llegó un enlace por cada alumno.")}
							</p>
						) : null}
						{info.cursos.length > 0 ? (
							<div className="dcCursos">
								{info.cursos.map((c, i) => (
									<div className="dcCurso" key={i}><b>{c.curso}</b><span>{horarioBonito(c.horario, { plural: true, separador: " " }) || c.horario}</span></div>
								))}
								<div className="dcPlantel">Washington Academy {info.plantel}</div>
							</div>
						) : null}
						<p className="dcNota">{t("Por favor, ten a la mano tu identificación.", "Por favor, tenga a la mano su identificación.")} Si la inscripción es de un menor de edad, por favor que la llene su mamá, papá o tutor.</p>
						{avanzado ? <p className="dcNota">{t("Ya llevas un avance: sigues donde te quedaste.", "Ya lleva un avance: sigue donde se quedó.")}</p> : null}
					</div>
				);
			}
			case "para": return pregunta("¿Para quién es la inscripción?", null,
				<Chips columnasUna opciones={[{ valor: "yo", texto: t("Para mí", "Para mí") }, { valor: "menor", texto: "Para mi hijo(a) o un menor de edad" }]} valor={para}
					onElegir={(v) => {
						setPara(v);
						guardar({ para: v });
						// Al elegir, sigue solo: a la identificación del alumno o, si es menor, a la del tutor.
						const esMenor = fechaValida(d.birthday) ? (edadDe(d.birthday) ?? 99) < 18 : v === "menor";
						window.setTimeout(() => setPaso(esMenor ? (fotos.tutor_frente ? "tutor" : "fotoTutor") : (fotos.id_frente ? "datos" : "fotoId")), 220);
					}} />);
			case "fotoId": return pregunta(porOtro ? t("Toma una foto de la identificación de ", "Tome una foto de la identificación de ") + alumno : t("Toma una foto de tu identificación", "Tome una foto de su identificación"),
				porOtro ? t("Del frente, donde están su nombre y su foto. Con ella llenamos sus datos y tú solo los revisas.", "Del frente, donde están su nombre y su foto. Con ella llenamos sus datos y usted solo los revisa.")
					: t("Del frente, donde están tu nombre y tu foto. Con ella llenamos tus datos y tú solo los revisas.", "Del frente, donde están su nombre y su foto. Con ella llenamos sus datos y usted solo los revisa."),
				<>
					<div className="dcFotos"><Foto ranura="id_frente" titulo={info.paisPlantel === 1 ? "INE, pasaporte o CURP" : "DNI o pasaporte"} /></div>
					<p className="dcNota">{t("Que se lea bien: con buena luz, sin reflejos y sin cortar las orillas.", "Que se lea bien: con buena luz, sin reflejos y sin cortar las orillas.")}</p>
					{!fotos.id_frente ? <button type="button" className="dcLink" onClick={() => setPaso("datos")}>{t("No la tengo a la mano: escribir los datos", "No la tengo a la mano: escribir los datos")}</button> : null}
				</>);
			case "datos": {
				const leidos = !!leido && !menor;
				const paraQuien = porOtro ? (menor ? "del alumno" : "de " + alumno) : "";
				return pregunta(leidos ? (porOtro ? "¿Están bien los datos de " + alumno + "?" : t("¿Están bien tus datos?", "¿Están bien sus datos?"))
						: porOtro ? "Datos " + paraQuien : t("Tus datos", "Sus datos"),
					leidos ? t("Los leímos de la identificación. Revisa que estén completos y, si algo no está igual, corrígelo.", "Los leímos de la identificación. Revise que estén completos y, si algo no está igual, corríjalo.")
						: menor ? (info.paisPlantel === 1 ? "Tal como aparecen en su acta de nacimiento." : "Tal como aparecen en su partida de nacimiento.")
						: porOtro ? "Tal como aparecen en su identificación." : t("Tal como aparecen en tu identificación.", "Tal como aparecen en su identificación."),
					<div className="dcDatos">
						<Dato etiqueta="Nombres (todos)" enId={difiere("firstName")} onUsar={() => cambiar({ firstName: leido?.firstName })}>
							<input ref={primerCampo} className="dcInput" autoComplete={porOtro ? "off" : "given-name"} autoCapitalize="words" placeholder="Ej. José Andrés" value={d.firstName || ""} onChange={(e) => cambiar({ firstName: e.target.value })} onKeyDown={alEnter} />
						</Dato>
						<Dato etiqueta="Apellidos (los dos)" enId={difiere("lastName")} onUsar={() => cambiar({ lastName: leido?.lastName })}>
							<input className="dcInput" autoComplete={porOtro ? "off" : "family-name"} autoCapitalize="words" placeholder="Ej. Aguilar Banegas" value={d.lastName || ""} onChange={(e) => cambiar({ lastName: e.target.value })} onKeyDown={alEnter} />
						</Dato>
						<Dato etiqueta="Fecha de nacimiento" enId={leido?.birthday && leido.birthday !== d.birthday ? fechaLarga(leido.birthday) : null}
							onUsar={() => { if (leido?.birthday) { const [y, m, dd] = leido.birthday.split("-"); ponerFecha(String(Number(dd)), String(Number(m)), y); } }}>
							<div className="dcFecha">
								<input className="dcInput" inputMode="numeric" maxLength={2} placeholder="Día" aria-label="Día" value={dia} onChange={(e) => ponerFecha(e.target.value.replace(/\D/g, ""), mes, anio)} onKeyDown={alEnter} />
								<span className="dcSelectCaja"><select className="dcInput" aria-label="Mes" value={mes} onChange={(e) => ponerFecha(dia, e.target.value, anio)}>
									<option value="">Mes</option>{MESES.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
								</select></span>
								<input className="dcInput" inputMode="numeric" maxLength={4} placeholder="Año" aria-label="Año" value={anio} onChange={(e) => ponerFecha(dia, mes, e.target.value.replace(/\D/g, ""))} onKeyDown={alEnter} />
							</div>
						</Dato>
						<Dato etiqueta="Género">
							<Chips opciones={info.catalogos.generos.map((g) => ({ valor: g.id, texto: g.name }))} valor={d.genderId ?? null} onElegir={(v) => cambiar({ genderId: v })} />
						</Dato>
						<Dato etiqueta={menor ? (info.paisPlantel === 1 ? "CURP" : "Número de identidad (viene en su partida de nacimiento)") : tipo && !verTipos ? "Número de " + tipo.name : "Documento"} enId={difiere("identificationCode")} onUsar={() => cambiar({ identificationTypeId: leido?.identificationTypeId ?? d.identificationTypeId, identificationCode: leido?.identificationCode })}>
							{tipo && !verTipos ? null : <Chips opciones={info.catalogos.tiposId.map((x) => ({ valor: x.id, texto: x.name }))} valor={d.identificationTypeId ?? null} onElegir={(v) => { cambiar({ identificationTypeId: v }); setVerTipos(false); }} />}
							{tipo ? <input className={"dcInput" + (verTipos ? " dcEspacio" : "")} inputMode={tipo.bool_only_digits ? "numeric" : "text"} autoCapitalize="characters"
								placeholder={tipo.name === "DNI" ? "0801199812345" : "Número de " + tipo.name} value={d.identificationCode || ""}
								onChange={(e) => cambiar({ identificationCode: tipo.bool_only_digits ? e.target.value.replace(/\D/g, "") : e.target.value.toUpperCase().replace(/\s+/g, "") })} onKeyDown={alEnter} /> : null}
							{tipo && !verTipos && !menor ? <button type="button" className="dcLinkChico" onClick={() => setVerTipos(true)}>¿Es otro documento?</button> : null}
						</Dato>
						<Dato etiqueta="Nacionalidad">
							<Chips opciones={[{ valor: info.paisPlantel, texto: pais?.name || "Mi país" }, { valor: -1, texto: "Otra" }]} valor={otraNacion ? -1 : d.nationalityId ?? null}
								onElegir={(v) => { if (v === -1) { setOtraNacion(true); cambiar({ nationalityId: null }); } else { setOtraNacion(false); cambiar({ nationalityId: v }); } }} />
							{otraNacion ? (
								<span className="dcSelectCaja dcEspacio"><select className="dcInput" value={d.nationalityId || ""} onChange={(e) => cambiar({ nationalityId: Number(e.target.value) || null })}>
									<option value="">Elegir país</option>{info.catalogos.paises.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
								</select></span>
							) : null}
						</Dato>
					</div>);
			}
			case "fotoTutor": return pregunta(t("Toma una foto de tu identificación", "Tome una foto de su identificación"),
				t("La tuya, como mamá, papá o tutor: del frente, donde están tu nombre y tu foto.", "La suya, como mamá, papá o tutor: del frente, donde están su nombre y su foto."),
				<>
					<div className="dcFotos"><Foto ranura="tutor_frente" titulo={info.paisPlantel === 1 ? "INE o pasaporte" : "DNI o pasaporte"} /></div>
					<p className="dcNota">{t("Que se lea bien: con buena luz, sin reflejos y sin cortar las orillas.", "Que se lea bien: con buena luz, sin reflejos y sin cortar las orillas.")}</p>
				</>);
			case "tutor": return pregunta(t("¿Cuál es tu nombre?", "¿Cuál es su nombre?"),
				nota && nota.paso === "tutor" ? null : t("Tú llenas los datos del alumno y firmas como mamá, papá o tutor.", "Usted llena los datos del alumno y firma como mamá, papá o tutor."),
				<>
					<Dato etiqueta="Nombre completo">
						<input ref={primerCampo} className="dcInput" autoComplete="name" autoCapitalize="words" placeholder="Nombre y apellidos" value={contacto.name} onChange={(e) => cambiarContacto({ name: e.target.value })} onKeyDown={alEnter} />
					</Dato>
					<Dato etiqueta={t("¿Qué eres del alumno?", "¿Qué es usted del alumno?")}>
						<Chips opciones={PARENTESCOS_TUTOR.map((x) => ({ valor: x, texto: x }))} valor={contacto.relation} onElegir={(v) => cambiarContacto({ relation: v })} />
					</Dato>
					<Dato etiqueta={t("Tu teléfono", "Su teléfono")}>
						<input className="dcInput" type="tel" inputMode="tel" autoComplete="tel" value={contacto.contactMedia} onChange={(e) => cambiarContacto({ contactMedia: e.target.value.replace(/[^\d+ ]/g, "") })} onKeyDown={alEnter} />
					</Dato>
				</>);
			case "correo": return pregunta(porOtro ? "¿A qué correo mandamos los avisos?" : t("¿Cuál es tu correo?", "¿Cuál es su correo?"), porOtro ? "Puede ser " + t("el tuyo", "el suyo") + "." : "Ahí llegan los avisos de la escuela.",
				<input ref={primerCampo} className="dcInput" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" placeholder="nombre@gmail.com" value={d.email || ""} onChange={(e) => cambiar({ email: e.target.value.trim() })} onKeyDown={alEnter} />);
			case "domicilio": return pregunta(porOtro ? "¿Dónde vive " + alumno + "?" : t("¿Dónde vives?", "¿Dónde vive?"), null,
				<div className="dcDatos">
					<Dato etiqueta="País">
						<Chips opciones={[{ valor: info.paisPlantel, texto: pais?.name || "Mi país" }, { valor: -1, texto: "Otro país" }]} valor={otroPais ? -1 : d.countryId ?? null}
							onElegir={(v) => { if (v === -1) { setOtroPais(true); cambiar({ countryId: null, stateId: null, cityId: null }); } else { setOtroPais(false); cambiar({ countryId: v }); } }} />
						{otroPais ? (
							<span className="dcSelectCaja dcEspacio"><select className="dcInput" value={d.countryId || ""} onChange={(e) => cambiar({ countryId: Number(e.target.value) || null, stateId: null, cityId: null })}>
								<option value="">Elegir país</option>{info.catalogos.paises.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
							</select></span>
						) : null}
					</Dato>
					{conEstados ? (
						<Dato etiqueta={(info.paisPlantel === 1 ? "Estado" : "Departamento") + " y ciudad"}>
							<div className="dcDos">
								<span className="dcSelectCaja"><select className="dcInput" aria-label={info.paisPlantel === 1 ? "Estado" : "Departamento"} value={d.stateId || ""} onChange={(e) => {
									const id = Number(e.target.value) || null;
									cambiar({ stateId: id, cityId: null, stateName: info.catalogos.estados.find((s) => s.id === id)?.name || "" });
								}}>
									<option value="">{info.paisPlantel === 1 ? "Estado" : "Departamento"}</option>{info.catalogos.estados.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
								</select></span>
								<span className="dcSelectCaja"><select className="dcInput" aria-label="Ciudad" disabled={!d.stateId} value={d.cityId || ""} onChange={(e) => {
									const id = Number(e.target.value) || null;
									cambiar({ cityId: id, cityName: ciudades.find((c) => c.id === id)?.name || "" });
								}}>
									<option value="">Ciudad o municipio</option>{ciudades.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
								</select></span>
							</div>
						</Dato>
					) : null}
					<Dato etiqueta="Dirección">
						<input className="dcInput" autoComplete="street-address" placeholder={info.paisPlantel === 1 ? "Col. Centro, calle Juárez 123" : "Col. Kennedy, bloque F, casa 12"} value={d.address || ""} onChange={(e) => cambiar({ address: e.target.value })} onKeyDown={alEnter} />
					</Dato>
				</div>);
			case "estudios": return pregunta(porOtro ? "¿Hasta dónde ha estudiado " + alumno + "?" : t("¿Hasta dónde estudiaste?", "¿Hasta dónde estudió?"), "El último grado que " + (porOtro ? "terminó" : t("terminaste", "terminó")) + ".",
				<>
					<Chips columnas opciones={info.catalogos.grados.map((g) => ({ valor: g.id, texto: g.name.replace(/\//g, "/\u200b") }))} valor={d.academicDegreeId ?? null} onElegir={(v) => cambiar({ academicDegreeId: v })} />
					<div className="dcEspacio">
						<Dato etiqueta={porOtro ? "¿En qué escuela o colegio estudió por última vez? (opcional)" : t("¿En qué escuela o colegio estudiaste por última vez? (opcional)", "¿En qué escuela o colegio estudió por última vez? (opcional)")}>
							<input className="dcInput" autoCapitalize="words" placeholder={info.paisPlantel === 1 ? "Ej. Secundaria Técnica 5" : "Ej. Instituto San José"} value={d.lastSchoolName || ""} onChange={(e) => cambiar({ lastSchoolName: e.target.value })} onKeyDown={alEnter} />
						</Dato>
					</div>
				</>);
			case "ocupacion": return pregunta(porOtro ? "¿A qué se dedica " + alumno + "?" : t("¿A qué te dedicas?", "¿A qué se dedica?"),
				porOtro ? "Su trabajo u oficio, o lo que estudia ahora." : t("Tu trabajo u oficio, o lo que estudias ahora.", "Su trabajo u oficio, o lo que estudia ahora."),
				<input ref={primerCampo} className="dcInput" autoCapitalize="sentences" placeholder={t("Escríbelo aquí", "Escríbalo aquí")} value={d.currentOccupation || ""} onChange={(e) => cambiar({ currentOccupation: e.target.value })} onKeyDown={alEnter} />);
			case "contacto": return pregunta(t("¿A quién llamamos en una emergencia?", "¿A quién llamamos en una emergencia?"),
				porOtro ? "Alguien de confianza que no sea " + alumno + t(". Puedes ser tú.", ". Puede ser usted.") : "Alguien de confianza que no sea " + t("tú", "usted") + ".",
				<>
					<Dato etiqueta="Nombre completo">
						<input ref={primerCampo} className="dcInput" autoCapitalize="words" placeholder="Nombre y apellido" value={contacto.name} onChange={(e) => cambiarContacto({ name: e.target.value })} onKeyDown={alEnter} />
					</Dato>
					<Dato etiqueta="Parentesco">
						<Chips opciones={PARENTESCOS.map((x) => ({ valor: x, texto: x }))} valor={contacto.relation} onElegir={(v) => cambiarContacto({ relation: v })} />
					</Dato>
					<Dato etiqueta="Teléfono">
						<input className="dcInput" type="tel" inputMode="tel" autoComplete="tel" value={contacto.contactMedia} onChange={(e) => cambiarContacto({ contactMedia: e.target.value.replace(/[^\d+ ]/g, "") })} onKeyDown={alEnter} />
					</Dato>
				</>);
			case "revisar": {
				const fila = (etiqueta: string, valor: string, ir: Paso) => (
					<button type="button" className="dcFila" onClick={() => setPaso(ir)} key={etiqueta}>
						<span>{etiqueta}</span><b>{valor || "—"}</b><i aria-hidden="true">Cambiar</i>
					</button>
				);
				const nombreDe = (lista: Opcion[], id?: number | null) => lista.find((x) => x.id === id)?.name || "";
				return pregunta(porOtro ? t("Revisa los datos", "Revise los datos") : t("Revisa tus datos", "Revise sus datos"), t("Toca cualquiera para corregirlo.", "Toque cualquiera para corregirlo."),
					<>
						{faltan.length > 0 ? <div className="dcFaltan"><b>{t("Por favor, antes de enviar corrige esto:", "Por favor, antes de enviar corrija esto:")}</b>{faltan.map((f) => <div key={f.texto}>{f.texto}</div>)}</div> : null}
						<div className="dcResumen">
							{menor ? fila("Tutor", contacto.name ? `${contacto.name} (${contacto.relation}) · ${telBonito(contacto.contactMedia)}` : "", "tutor") : null}
							{fila(menor ? "Identificación del tutor" : "Identificación", (menor ? fotos.tutor_frente : fotos.id_frente) ? "Foto lista" : "", menor ? "fotoTutor" : "fotoId")}
							{fila(porOtro ? "Alumno" : "Nombre", ((d.firstName || "") + " " + (d.lastName || "")).trim(), "datos")}
							{fila("Nacimiento", fechaValida(d.birthday) ? fechaLarga(d.birthday) : "", "datos")}
							{fila("Género", nombreDe(info.catalogos.generos, d.genderId), "datos")}
							{fila(tipo?.name || "Documento", d.identificationCode || "", "datos")}
							{fila("Nacionalidad", nombreDe(info.catalogos.paises, d.nationalityId), "datos")}
							{fila("Correo", d.email || "", "correo")}
							{fila("Vive en", [d.address, d.cityName, d.stateName, otroPais ? nombreDe(info.catalogos.paises, d.countryId) : ""].filter(Boolean).join(", "), "domicilio")}
							{fila("Estudios", [nombreDe(info.catalogos.grados, d.academicDegreeId), d.lastSchoolName].filter(Boolean).join(" · "), "estudios")}
							{fila("Ocupación", d.currentOccupation || "", "ocupacion")}
							{!menor ? fila("Emergencia", contacto.name ? `${contacto.name} (${contacto.relation}) · ${telBonito(contacto.contactMedia)}` : "", "contacto") : null}
						</div>
					</>);
			}
			case "listo": return (
				<div className="dcPregunta dcListo" key="listo">
					<div className="dcPalomita" aria-hidden="true">✓</div>
					<h1 className="dcTitulo grande">{"¡Listo" + (nombre ? ", " + nombre : "") + "!"}</h1>
					<p className="dcAyuda">{porOtro
						? t("Muchas gracias. Recibimos los datos de " + alumno + "; Admisiones revisa la matrícula y te escribimos por WhatsApp.", "Muchas gracias. Recibimos los datos de " + alumno + "; Admisiones revisa la matrícula y le escribimos por WhatsApp.")
						: t("Muchas gracias. Recibimos tus datos; Admisiones revisa tu matrícula y te escribimos por WhatsApp.", "Muchas gracias. Recibimos sus datos; Admisiones revisa su matrícula y le escribimos por WhatsApp.")}</p>
					{info.grupo && info.grupo.faltanOtros > 0 ? (
						<p className="dcNota dcGrupo">{(info.grupo.faltanOtros === 1 ? "Falta otro alumno" : "Faltan " + info.grupo.faltanOtros + " alumnos más") + t(": abre su enlace en el chat de WhatsApp, por favor.", ": abra su enlace en el chat de WhatsApp, por favor.")}</p>
					) : null}
					<p className="dcNota">{t("Gracias por ser parte de Washington Academy. Ya puedes cerrar esta página.", "Gracias por ser parte de Washington Academy. Ya puede cerrar esta página.")}</p>
				</div>
			);
		}
	};

	const enRevisar = paso === "revisar";
	return (
		<div className="dc">
			<div className="dcCabeza">
				<img src={logo} alt="Washington Academy" className="dcLogo" />
				{paso !== "listo" && paso !== "bienvenida" ? <div className="dcBarra" aria-hidden="true"><span style={{ width: progreso + "%" }} /></div> : null}
			</div>
			<div className="dcCuerpo">{pantalla()}</div>
			{paso !== "listo" ? (
				<div className="dcPie">
					{aviso ? <div className="dcAviso" role="alert">{aviso}</div> : null}
					<div className="dcBotones">
						{indice > 0 ? <button type="button" className="dcAtras" onClick={atras} aria-label="Atrás">←</button> : null}
						<button type="button" className="dcSeguir" disabled={ocupado || subiendo !== null} onClick={() => siguiente()}>
							{ocupado ? <span className="dcGiro" /> : paso === "bienvenida" ? "Empezar" : enRevisar ? (porOtro ? "Enviar los datos" : "Enviar mis datos") : "Continuar"}
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default Datos;
